import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Dialog } from '@angular/cdk/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs';
import {
  DeviceLicenseManageModalComponent,
  DeviceLicenseManageModalResult,
} from '../device-license-manage-modal/device-license-manage-modal.component';
import {
  LicenseBatchDto,
  ReleaseBundleDto,
} from 'src/app/models/license.model';
import {
  ArtifactResponse,
  SoftwareSelectionResolutionDto,
} from 'src/app/models/device.model';
import {
  managesDevicesAcrossCompanies,
  UserRole,
} from 'src/app/models/user.model';
import { DeviceService } from 'src/app/services/device.service';
import { ProfileState } from 'src/app/stores/store.states';

@Component({
  selector: 'app-device-license-segment',
  templateUrl: './device-license-segment.component.html',
  styleUrls: ['./device-license-segment.component.scss'],
  standalone: false,
})
export class DeviceLicenseSegmentComponent implements OnChanges {
  @Input() deviceId: string | null = null;
  @Input() deviceName: string | null = null;
  @Input() companyId: string | null = null;
  @Input() licenseBatches?: LicenseBatchDto[] | null;
  @Input() artifactOverrides?: ArtifactResponse[] | null;
  @Input() bundleOverrides?: ReleaseBundleDto[] | null;
  @Input() unresolvedOverrideIds?: string[] | null;
  @Input() softwareSelectionResolutions?:
    | SoftwareSelectionResolutionDto[]
    | null;
  @Input() profile: ProfileState | null = null;
  @Output() licenseBatchesChange = new EventEmitter<LicenseBatchDto[]>();
  @Output() deviceRefreshRequested = new EventEmitter<void>();

  linkedLicenseBatches: LicenseBatchDto[] = [];
  loading = false;
  errorMessage = '';

  protected readonly UserRole = UserRole;
  private readonly destroyRef = inject(DestroyRef);
  private readonly deviceService = inject(DeviceService);
  private readonly dialog = inject(Dialog);
  private latestLookupDeviceId = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['licenseBatches']) {
      this.useInputLicenseBatches();
      return;
    }

    if (changes['deviceId'] && !this.hasInputLicenseBatches()) {
      this.loadLinkedLicenses();
    }
  }

  get canManageLicenses(): boolean {
    return (
      managesDevicesAcrossCompanies(this.profile) ||
      this.profile?.role === UserRole.ADMIN
    );
  }

  get manageLicensesTooltip(): string {
    return this.canManageLicenses ? '' : 'Only admin can manage licenses.';
  }

  get supportedComponentIds(): string[] {
    return [
      ...new Set(
        this.linkedLicenseBatches.flatMap((batch) =>
          (batch.product?.licensedComponents ?? []).map(
            (component) => component.id,
          ),
        ),
      ),
    ];
  }

  manageLicenses(): void {
    if (!this.canManageLicenses) {
      return;
    }

    const dialogRef = this.dialog.open<DeviceLicenseManageModalResult>(
      DeviceLicenseManageModalComponent,
      {
        data: {
          deviceId: this.deviceId?.trim() ?? '',
          deviceName: this.deviceName?.trim() ?? '',
          companyId: this.companyId?.trim() ?? '',
          linkedLicenseBatches: this.linkedLicenseBatches,
          artifactOverrides: this.artifactOverrides ?? [],
          bundleOverrides: this.bundleOverrides ?? [],
          unresolvedOverrideIds: this.unresolvedOverrideIds ?? [],
          isMasterAdmin: this.profile?.isMasteradmin ?? false,
          managesDevicesAcrossCompanies: managesDevicesAcrossCompanies(
            this.profile,
          ),
        },
      },
    );

    dialogRef.closed.pipe(take(1)).subscribe((result) => {
      if (!result?.linkedLicenseBatches) {
        // A backdrop or Escape close does not return the modal result. Refresh
        // defensively because a completed assignment may still have changed
        // the device while the modal was open.
        this.deviceRefreshRequested.emit();
        return;
      }

      this.linkedLicenseBatches = this.normalizeLicenseBatches(
        result.linkedLicenseBatches,
      );
      if (result.artifactOverrides) {
        this.artifactOverrides = result.artifactOverrides;
      }
      if (result.bundleOverrides) {
        this.bundleOverrides = result.bundleOverrides;
      }
      if (result.unresolvedOverrideIds) {
        this.unresolvedOverrideIds = result.unresolvedOverrideIds;
      }
      if (result.softwareSelectionResolutions) {
        this.softwareSelectionResolutions = result.softwareSelectionResolutions;
      }
      this.licenseBatchesChange.emit(this.linkedLicenseBatches);
      if (result.licenseAssignmentsChanged) {
        this.deviceRefreshRequested.emit();
      }
    });
  }

  private loadLinkedLicenses(): void {
    if (this.hasInputLicenseBatches()) {
      this.useInputLicenseBatches();
      return;
    }

    const normalizedDeviceId = this.deviceId?.trim() ?? '';
    this.latestLookupDeviceId = normalizedDeviceId;
    this.linkedLicenseBatches = [];
    this.errorMessage = '';

    if (!normalizedDeviceId) {
      this.loading = false;
      return;
    }

    this.loading = true;
    this.deviceService
      .getDevice(normalizedDeviceId, 'FULL')
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (device) => {
          if (this.latestLookupDeviceId !== normalizedDeviceId) {
            return;
          }

          this.linkedLicenseBatches = this.normalizeLicenseBatches(
            device.licenseBatches ?? [],
          );
          this.artifactOverrides =
            device.softwareVersionOverrides?.artifactOverrides ?? [];
          this.bundleOverrides =
            device.softwareVersionOverrides?.bundleOverrides ?? [];
          this.unresolvedOverrideIds =
            device.softwareVersionOverrides?.unresolvedOverrideIds ?? [];
          this.softwareSelectionResolutions =
            device.licenseRedemptionInfo?.components ?? [];
          if (!this.companyId) {
            this.companyId = device.company?.id?.trim() || null;
          }
          this.loading = false;
        },
        error: () => {
          if (this.latestLookupDeviceId !== normalizedDeviceId) {
            return;
          }

          this.linkedLicenseBatches = [];
          this.errorMessage = 'Failed to load linked licenses.';
          this.loading = false;
        },
      });
  }

  private hasInputLicenseBatches(): boolean {
    return Array.isArray(this.licenseBatches);
  }

  private useInputLicenseBatches(): void {
    if (!this.hasInputLicenseBatches()) {
      this.loadLinkedLicenses();
      return;
    }

    this.latestLookupDeviceId = '';
    this.loading = false;
    this.errorMessage = '';
    this.linkedLicenseBatches = this.normalizeLicenseBatches(
      this.licenseBatches ?? [],
    );
  }

  private normalizeLicenseBatches(
    batches: LicenseBatchDto[],
  ): LicenseBatchDto[] {
    return batches.map((batch) => ({
      ...batch,
      scope: batch.scope ?? { id: '', name: '' },
    }));
  }
}
